import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAcademySlug } from '../../../../hooks/useAcademySlug';
import toast from 'react-hot-toast';
import type { ProRataPreviewResponse } from '@court-sync/shared';
import { formatCurrencySafe } from '@court-sync/shared';
import { useAuthStore } from '../../../../store/auth';
import { usePublicBookingStore } from '../../../../store/publicBooking';
import { isPerSessionPublicBooking } from '../publicBookingSessionMode';
import { PublicBookingPaymentStep } from '../PublicBookingPaymentStep';
import { publicBookingStepPath } from '../bookingPaths';
import { lessonService } from '../../../../services/lesson';

export function BookingPaymentStep() {
  const academySlug = useAcademySlug();
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const { user: authStoreUser } = useAuthStore();
  const userFormData = usePublicBookingStore((s) => s.userFormData);
  const lesson = usePublicBookingStore((s) => s.lesson);
  const booking = usePublicBookingStore((s) => s.booking);
  const allBookings = usePublicBookingStore((s) => s.allBookings);
  const stripePromise = usePublicBookingStore((s) => s.stripePromise);
  const hasPaymentProcessing = usePublicBookingStore((s) => s.hasPaymentProcessing);
  const markPaymentMade = usePublicBookingStore((s) => s.markPaymentMade);
  const campBookingMode = usePublicBookingStore((s) => s.campBookingMode);
  const groupBookingMode = usePublicBookingStore((s) => s.groupBookingMode);
  const academy = usePublicBookingStore((s) => s.academy);

  const isPerSessionBooking = isPerSessionPublicBooking(
    lesson,
    campBookingMode,
    groupBookingMode
  );

  const [proRataPaymentLabel, setProRataPaymentLabel] = useState<{
    lessonId: string;
    label: string;
  } | null>(null);

  const shouldFetchProRataLabel =
    !!lesson &&
    !!academy?.proRataEnabled &&
    lesson.lessonType === 'group' &&
    lesson.pricingType !== 'per_session' &&
    !isPerSessionBooking;

  useEffect(() => {
    if (!shouldFetchProRataLabel || !lesson || !academy) {
      return undefined;
    }
    let cancelled = false;
    const lid = lesson.id;
    void lessonService
      .getProRataPreview(lid, academy.id)
      .then((p: ProRataPreviewResponse) => {
        if (cancelled) return;
        if (p.isProRated && p.totalSessions > 0) {
          setProRataPaymentLabel({
            lessonId: lid,
            label: `${p.remainingSessions} of ${p.totalSessions} sessions (pro-rated)`,
          });
        } else {
          setProRataPaymentLabel({ lessonId: lid, label: '' });
        }
      })
      .catch(() => {
        if (!cancelled) setProRataPaymentLabel(null);
      });
    return () => {
      cancelled = true;
    };
  }, [shouldFetchProRataLabel, lesson?.id, academy?.id]);

  const proRataSubLabel =
    shouldFetchProRataLabel &&
    lesson &&
    proRataPaymentLabel?.lessonId === lesson.id &&
    proRataPaymentLabel.label
      ? proRataPaymentLabel.label
      : undefined;

  const isDailyCampBooking =
    lesson?.lessonType === 'camp' && (campBookingMode ?? 'daily') === 'daily';
  const isGroupSessionBooking =
    lesson?.lessonType === 'group' &&
    (groupBookingMode ?? 'full') === 'session' &&
    lesson.sessionPrice != null;

  const bookingsForPayment = useMemo(
    () => (allBookings.length > 0 ? allBookings : booking ? [booking] : []),
    [allBookings, booking]
  );

  const paymentPriceSubtotalLabel = useMemo(() => {
    if (proRataSubLabel) return proRataSubLabel;
    if (
      !lesson ||
      !booking ||
      !(booking.sessionDate != null || allBookings.some((b) => b.sessionDate))
    ) {
      if (
        isDailyCampBooking &&
        bookingsForPayment.length === 1 &&
        bookingsForPayment[0].campBookingType === 'full_camp'
      ) {
        return 'Full camp package';
      }
      return undefined;
    }
    if (isGroupSessionBooking && lesson.sessionPrice != null) {
      const subtotal = bookingsForPayment.reduce((sum, b) => sum + (b.totalAmount ?? 0), 0);
      const unitStr = formatCurrencySafe(lesson.sessionPrice, lesson.currency);
      const sessionCount =
        lesson.sessionPrice > 0 && subtotal > 0
          ? Math.max(1, Math.round(subtotal / lesson.sessionPrice))
          : Math.max(1, bookingsForPayment.length);
      return sessionCount === 1
        ? `1 session × ${unitStr}`
        : `${sessionCount} sessions × ${unitStr}`;
    }
    if (
      !isDailyCampBooking ||
      lesson.campDayPrice == null
    ) {
      return undefined;
    }
    const subtotal = bookingsForPayment.reduce((sum, b) => sum + (b.totalAmount ?? 0), 0);
    const unitStr = formatCurrencySafe(lesson.campDayPrice, lesson.currency);
    const sessionCount =
      lesson.campDayPrice > 0 && subtotal > 0
        ? Math.max(1, Math.round(subtotal / lesson.campDayPrice))
        : Math.max(1, bookingsForPayment.length);
    return sessionCount === 1
      ? `1 camp day × ${unitStr}`
      : `${sessionCount} camp days × ${unitStr}`;
  }, [
    proRataSubLabel,
    lesson,
    booking,
    isDailyCampBooking,
    isGroupSessionBooking,
    allBookings,
    bookingsForPayment,
  ]);

  React.useEffect(() => {
    if (!booking && academySlug && lessonId) {
      navigate(publicBookingStepPath(academySlug, lessonId, 'details'), { replace: true });
    }
  }, [booking, academySlug, lessonId, navigate]);

  React.useEffect(() => {
    if (!hasPaymentProcessing && booking && academySlug && lessonId) {
      navigate(publicBookingStepPath(academySlug, lessonId, 'success'), { replace: true });
    }
  }, [hasPaymentProcessing, booking, academySlug, lessonId, navigate]);

  if (!lesson || !booking || !academySlug || !lessonId) {
    return null;
  }

  return (
    <PublicBookingPaymentStep
      stripePromise={stripePromise}
      bookingId={booking.id}
      bookingIds={allBookings.length > 1 ? allBookings.map((b) => b.id) : undefined}
      amount={
        allBookings.length > 1
          ? allBookings.reduce((sum, b) => sum + (b.totalAmount ?? 0), 0)
          : (booking.totalAmount ?? lesson.price)
      }
      priceSubtotalLabel={paymentPriceSubtotalLabel}
      customerEmail={authStoreUser?.email ?? userFormData?.email ?? booking.customerEmail ?? ''}
      isAuthenticated={!!authStoreUser}
      onPaymentSuccess={() => {
        markPaymentMade();
        toast.success('Payment successful! Your lesson is booked.');
        navigate(publicBookingStepPath(academySlug, lessonId, 'success'));
      }}
      onPaymentError={(err) => toast.error(err)}
    />
  );
}
